/**
 * The Memory control's picture (D340, docs/plans/2026-10-memory-globe.md): the globe, the map (Rings, Force, Circle, Hex)
 * and the Timeline on one canvas, one set of nodes. Switching picture glides every node from where it was to where it
 * goes; a click flies the camera to the node and draws its connections; the world drifts and orbits when Motion is on.
 *
 * Adapted from RUBRIC Agentic OS v2's Second Brain (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md): brain/port.js
 * draws every picture on one canvas and blends the places between them; `_core.js` flies to a node on select and draws
 * its links. The globe, map and Timeline are one object seen three ways, not three components.
 *
 * The engine (`memoryBrainEngine.ts`) says where everything is; the painter (`memoryBrainPaint.ts`) draws it.
 */
import { type RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { beginTransition, carryOver, centreOn, computeTargets, createEngine, type Engine, fitFlat, flyFlatCamera, flyGlobeCamera, nodePosition, pickNode, placeFresh, settle, type Stage } from './memoryBrainEngine.js';
import { type LabelHit,paint } from './memoryBrainPaint.js';
import { wheelZoomsPicture } from './memoryFocusModel.js';
import type { GlobeColours } from './memoryGlobeColours.js';
import { type Connection, connectionsOf, flyTo, type Globe, type GlobeLayer, stepRings } from './memoryGlobeModel.js';
import { blendCamera, } from './memoryGlobeModel.js';
import { layoutMap, type MapLayoutKind, ringsPlace } from './memoryMapLayouts.js';
import { maskWithRim, type OpenFocus, openFocus, RIM_CAMERA, rimHidden } from './memoryRimModel.js';
import { buildTimeline } from './memoryTimelineModel.js';
import { easeOutCubic, FLY_MS, motionSpeedOf, TRANSITION_MS, tweenProgress } from './memoryTween.js';

export interface BrainHandle {
  fit: () => void;
  zoomBy: (factor: number) => void;
  /** Fly to a node by key: the globe turns it to face the viewer, the map centres on it, the Timeline scrolls to it. */
  flyToKey: (key: string) => void;
}

export interface BrainCanvasProps {
  globe: Globe;
  colours: GlobeColours;
  picture: 'globe' | 'map' | 'timeline';
  layoutKind: MapLayoutKind;
  /** The graph's links as node-index pairs: drawn faint on the map. */
  links: ReadonlyArray<readonly [number, number]>;
  /** The links between layers as node-index pairs: drawn dashed on the map. */
  xlinks: ReadonlyArray<readonly [number, number]>;
  /** What a closed folder hides (`foldHidden`), the folders that are open, and a counter that moves when they change. */
  fold: Uint8Array;
  expanded: ReadonlySet<string>;
  foldVersion: number;
  /** A legend entry's group: the nodes it holds stay lit and the rest step back. */
  focus: ReadonlySet<number> | null;
  /** A connection pointed at in the card: its key and the link's words. */
  litLink: { key: string; relation: string } | null;
  adjacency: ReadonlyArray<readonly number[]>;
  selectedKey: string | null;
  pointedKey: string | null;
  foundKeys: ReadonlySet<string> | null;
  offLayers: ReadonlySet<GlobeLayer>;
  offDepts: ReadonlySet<number>;
  insetLeft: number;
  insetRight: number;
  /** Nodes drift, orbit and the rings turn; the slider's speed, 0 to 1. */
  motion: boolean;
  speed: number;
  reducedMotion: boolean;
  /** Where the logo sits: the canvas moves this element's transform to the centre of the picture every frame. */
  centreRef: RefObject<HTMLDivElement | null>;
  centreFile: string;
  centreOwner?: string;
  /** "As it was on" and the instruction file's edits, for the Timeline. */
  asOf?: number;
  now: number;
  /** Left out where there is no instruction file: the Timeline then has no lane for it. */
  instructionEdits?: readonly number[];
  handleRef?: RefObject<BrainHandle | null>;
  onSelect: (key: string | null) => void;
  onOpen: (key: string) => void;
  /** A layer's name was pressed. */
  onLayerPress: (layer: GlobeLayer) => void;
  /** Back / Escape: close one opened level; true when it did, so the selection is kept. */
  onBack?: () => boolean;
  /** How tall the stage's header is: the picture centres below it. */
  headerH?: number;
}

const HEADER = 62;
const FOOT = 84;

export function MemoryBrainCanvas(p: BrainCanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const live = useRef(p);
  const engineRef = useRef<Engine | null>(null);
  const labelsRef = useRef<LabelHit[]>([]);
  const geometry = useRef<{ stage: Stage; map: ReturnType<typeof layoutMap> | null; tl: ReturnType<typeof buildTimeline> | null; tlX: number; fold: Uint8Array; rimFocus: OpenFocus | null }>({ stage: { W: 0, H: 0, insetLeft: 0, insetRight: 0, top: HEADER, bottom: FOOT }, map: null, tl: null, tlX: 0, fold: new Uint8Array(0), rimFocus: null });

  useEffect(() => {
    live.current = p;
    if (engineRef.current) engineRef.current.dirty = true;
  });

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const top = p.headerH ?? HEADER;
  const stage: Stage = useMemo(() => ({ W: size.w, H: size.h, insetLeft: p.insetLeft, insetRight: p.insetRight, top, bottom: FOOT }), [size.w, size.h, p.insetLeft, p.insetRight, top]);
  const room = Math.max(200, size.w - p.insetLeft - p.insetRight);
  const tlWidth = Math.max(360, room - 24);
  const tlX = p.insetLeft + 12;
  const sized = size.w > 0 && size.h > 0;
  const tz = -new Date().getTimezoneOffset();

  // A level that is open puts the rings and the shells in the rim state: the rest on a rim, the opened level's children on a wide ring.
  const rimFocus = useMemo(() => ((p.picture === 'map' && p.layoutKind === 'rings') || p.picture === 'globe' ? openFocus(p.globe, p.expanded, p.selectedKey) : null), [p.picture, p.layoutKind, p.globe, p.expanded, p.selectedKey]);
  const fold = useMemo(() => maskWithRim(p.fold, rimFocus ? rimHidden(p.globe, rimFocus) : null), [p.fold, p.globe, rimFocus]);
  const map = useMemo(() => (p.picture === 'map' ? layoutMap(p.globe, p.layoutKind, p.links, fold, rimFocus) : null), [p.picture, p.globe, p.layoutKind, p.links, fold, rimFocus]);
  const tl = useMemo(
    () =>
      p.picture === 'timeline' && sized
        ? buildTimeline(p.globe, { width: tlWidth, height: size.h - 52, top: 40, now: p.now, ...(p.asOf !== undefined ? { asOf: p.asOf } : {}), tzOffsetMinutes: tz, offLayers: p.offLayers, offDepartments: p.offDepts, ...(p.instructionEdits ? { instructionEdits: p.instructionEdits } : {}) })
        : null,
    [p.picture, p.globe, sized, tlWidth, size.h, p.now, p.asOf, tz, p.offLayers, p.offDepts, p.instructionEdits],
  );

  // One engine per set of nodes; the camera and the places of nodes that remain are kept when the nodes are refreshed.
  const globeRef = useRef<Globe | null>(null);
  const engine = useMemo(() => {
    const e = createEngine(p.globe);
    const old = engineRef.current;
    const oldGlobe = globeRef.current;
    if (old && oldGlobe) carryOver(e, old, oldGlobe, p.globe);
    else e.key = '';
    return e;
  }, [p.globe]);
  useEffect(() => {
    engineRef.current = engine;
    globeRef.current = p.globe;
  }, [engine, p.globe]);

  geometry.current = { stage, map, tl, tlX, fold, rimFocus };

  const motionOn = p.motion && !p.reducedMotion;

  // A change of picture or layout: keep where everything is, aim at the new places, and the camera at the new fit.
  useEffect(() => {
    if (!sized) return;
    const e = engine;
    const key = `${p.picture}|${p.picture === 'map' ? p.layoutKind : ''}`;
    const first = e.key === '';
    const changed = e.key !== key;
    if (!changed) return;
    const now = performance.now();
    if (!first) beginTransition(e, now, p.reducedMotion ? 0 : TRANSITION_MS);
    e.key = key;
    e.picture = p.picture;
    if (p.picture === 'map') e.flat = fitFlat(e, { stage, map, tl: null, picture: 'map', tlX });
    else if (p.picture === 'timeline') e.flat = { k: 1, x: 0, y: 0 };
    e.fitK = e.flat.k;
    e.flatTween = null;
    e.dirty = true;
    // Only a different picture or layout starts a transition; a resize or a new legend choice just redraws.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, sized, p.picture, p.layoutKind, map]);

  // A folder opened or closed: the nodes glide to their new places, the camera stays where it is.
  const lastFold = useRef(p.foldVersion);
  useEffect(() => {
    if (lastFold.current === p.foldVersion) return;
    lastFold.current = p.foldVersion;
    if (!sized) return;
    beginTransition(engine, performance.now(), p.reducedMotion ? 0 : 420);
    engine.dirty = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.foldVersion]);

  // The timeline's place in the room moves when a panel opens or closes: the days glide.
  useEffect(() => {
    if (!sized) return;
    if (p.picture === 'timeline' && engine.key === 'timeline|') beginTransition(engine, performance.now(), p.reducedMotion ? 0 : 320);
    engine.dirty = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tl]);
  useEffect(() => {
    if (!sized || p.picture !== 'map') return;
    // A room that changed (a panel opened) refits the map.
    const next = fitFlat(engine, { stage, map, tl: null, picture: 'map', tlX });
    engine.fitK = next.k;
    flyFlatCamera(engine, next, performance.now(), p.reducedMotion ? 0 : 420);
    engine.dirty = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.insetLeft, p.insetRight, size.w, size.h, top]);

  // A level opened, changed or closed: the camera refits to the new picture.
  const rimKey = map?.rim ? map.rim.focus : -1;
  const lastRim = useRef(rimKey);
  useEffect(() => {
    if (lastRim.current === rimKey) return;
    lastRim.current = rimKey;
    if (!sized || p.picture !== 'map') return;
    const next = fitFlat(engine, { stage, map, tl: null, picture: 'map', tlX });
    engine.fitK = next.k;
    flyFlatCamera(engine, next, performance.now(), p.reducedMotion ? 0 : 420);
    engine.dirty = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rimKey]);

  const selectedIndex = p.selectedKey ? (p.globe.byKey.get(p.selectedKey)?.index ?? -1) : -1;
  const connections: Connection[] = useMemo(() => (selectedIndex >= 0 ? connectionsOf(p.globe, p.adjacency, selectedIndex) : []), [p.globe, p.adjacency, selectedIndex]);
  const foundIdx = useMemo(() => {
    if (!p.foundKeys) return null;
    const s = new Set<number>();
    for (const k of p.foundKeys) {
      const n = p.globe.byKey.get(k);
      if (n) s.add(n.index);
    }
    return s;
  }, [p.foundKeys, p.globe]);

  /** The camera to a node: the globe turns to face it and comes closer, the flat pictures centre on it. */
  const flyToNode = useCallback(
    (index: number, instant = false) => {
      const e = engine;
      const g = p.globe;
      if (index < 0 || !sized) return;
      const now = performance.now();
      const dur = instant || p.reducedMotion ? 0 : FLY_MS;
      const node = g.nodes[index];
      if (p.picture === 'globe') {
        // With a level open the ring faces the viewer and the whole Universe stays in view: nothing is flown into.
        if (e.rim) {
          flyGlobeCamera(e, RIM_CAMERA, now, dur);
          e.dirty = true;
          return;
        }
        flyGlobeCamera(e, flyTo(nodePosition(e, g, node)), now, dur, index);
      } else if (p.picture === 'map' && map) {
        // Opening a level refits the picture to the rim state instead of zooming in on its hub.
        if (map.rim && (node.struct === 'group' || node.struct === 'dir')) return;
        const w = map.kind === 'rings' ? ringsPlace(map, index, e.ringRot) : { x: map.xs[index], y: map.ys[index] };
        flyFlatCamera(e, centreOn(w.x, w.y, Math.max(e.flat.k, 1.9), stage), now, dur);
      } else if (p.picture === 'timeline' && tl) {
        const pos = tl.positions.get(index);
        if (pos) flyFlatCamera(e, centreOn(pos.x + tlX, pos.y, Math.max(e.flat.k, 2.2), stage), now, dur);
      }
      e.dirty = true;
    },
    [engine, p.globe, p.picture, p.reducedMotion, map, tl, tlX, stage, sized],
  );

  // Fly to what was picked, from the canvas, from Find, or from Enter in the search.
  const lastFlown = useRef<string | null>(null);
  useEffect(() => {
    if (!p.selectedKey) {
      lastFlown.current = null;
      return;
    }
    if (lastFlown.current === p.selectedKey) return;
    lastFlown.current = p.selectedKey;
    flyToNode(selectedIndex);
    // Only a new selection flies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.selectedKey, sized]);

  useImperativeHandle(
    p.handleRef,
    () => ({
      flyToKey: (key: string) => {
        const node = p.globe.byKey.get(key);
        if (node) flyToNode(node.index);
      },
      fit: () => {
        const e = engine;
        const now = performance.now();
        const dur = p.reducedMotion ? 0 : FLY_MS;
        if (p.picture === 'globe') flyGlobeCamera(e, { yaw: e.cam.yaw, pitch: 0.3, zoom: 1, panX: 0, panY: 0 }, now, dur);
        else if (p.picture === 'map') flyFlatCamera(e, fitFlat(e, { stage, map, tl: null, picture: 'map', tlX }), now, dur);
        else flyFlatCamera(e, { k: 1, x: 0, y: 0 }, now, dur);
        e.dirty = true;
      },
      zoomBy: (f: number) => {
        const e = engine;
        if (p.picture === 'globe') {
          e.cam = { ...e.cam, zoom: Math.max(0.45, Math.min(9, e.cam.zoom * f)) };
          e.camTween = null;
        } else {
          const cx = p.insetLeft + room / 2;
          const cy = size.h / 2;
          const k = Math.max(0.02, Math.min(14, e.flat.k * f));
          const r = k / e.flat.k;
          e.flat = { k, x: cx - (cx - e.flat.x) * r, y: cy - (cy - e.flat.y) * r };
          e.flatTween = null;
        }
        e.dirty = true;
      },
    }),
    [engine, p.globe, p.picture, p.reducedMotion, p.insetLeft, room, size.h, map, stage, tlX, flyToNode],
  );

  // The frame loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !sized) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.w * dpr);
    canvas.height = Math.round(size.h * dpr);
    const ctx = canvas.getContext('2d')!;
    const font = getComputedStyle(canvas).fontFamily || 'Inter, system-ui, sans-serif';
    const e = engine;
    e.dirty = true;
    let raf = 0;
    let last = performance.now();
    let cost = 0;
    let drawn = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (document.hidden) {
        last = now;
        return;
      }
      const props = live.current;
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const geo = geometry.current;
      const moveOn = props.motion && !props.reducedMotion;
      const speed = motionSpeedOf(props.speed);
      if (moveOn) {
        e.clock += dt * speed;
        if (props.picture === 'globe') e.rings = stepRings(e.rings, dt * speed);
        if (props.picture === 'map') e.ringRot += dt * speed * 0.05;
        const selected = props.selectedKey != null;
        if (props.picture === 'globe' && !e.drag && !selected && !e.camTween) e.cam = { ...e.cam, yaw: e.cam.yaw + dt * speed * 0.045 };
      }
      // Camera tweens.
      let tweening = false;
      if (e.camTween) {
        const t = e.camTween;
        const prog = tweenProgress(t.t0, now, t.dur);
        const to = t.follow !== undefined ? flyTo(nodePosition(e, props.globe, props.globe.nodes[t.follow]), t.to.zoom) : t.to;
        e.cam = blendCamera(t.from, to, easeOutCubic(prog));
        if (prog >= 1) e.camTween = null;
        tweening = true;
      }
      if (e.flatTween) {
        const t = e.flatTween;
        const prog = easeOutCubic(tweenProgress(t.t0, now, t.dur));
        e.flat = { k: t.from.k * Math.pow(t.to.k / t.from.k, prog), x: t.from.x + (t.to.x - t.from.x) * prog, y: t.from.y + (t.to.y - t.from.y) * prog };
        if (prog >= 1) e.flatTween = null;
        tweening = true;
      }
      const need = moveOn || tweening || !!e.trans || e.dirty || !!e.drag;
      if (!need && e.A.every((a, i) => a === e.TA[i])) return;
      const t0 = performance.now();
      computeTargets(e, { globe: props.globe, stage: geo.stage, picture: props.picture, map: geo.map, tl: geo.tl, tlX: geo.tlX, offLayers: props.offLayers, offDepts: props.offDepts, fold: geo.fold, motion: moveOn, rimFocus: geo.rimFocus });
      placeFresh(e);
      const moving = settle(e, now, dt);
      e.dirty = false;
      const sel = props.selectedKey ? (props.globe.byKey.get(props.selectedKey)?.index ?? -1) : -1;
      const result = paint(ctx, e, {
        globe: props.globe,
        colours: props.colours,
        stage: geo.stage,
        picture: props.picture,
        map: geo.map,
        tl: geo.tl,
        tlX: geo.tlX,
        dpr,
        selected: sel,
        pointed: props.pointedKey ? (props.globe.byKey.get(props.pointedKey)?.index ?? -1) : -1,
        found: foundRef.current,
        connections: connectionsRef.current,
        litLink: litRef.current,
        focus: props.focus,
        expanded: props.expanded,
        wires: props.links,
        xlinks: props.xlinks,
        offLayers: props.offLayers,
        offDepts: props.offDepts,
        motion: moveOn,
        now: e.clock,
        wall: props.now,
        centreFile: props.centreFile,
        ...(props.centreOwner ? { centreOwner: props.centreOwner } : {}),
        font,
      });
      labelsRef.current = result.labels;
      if (moving) e.dirty = true;
      const anchor = props.centreRef.current;
      if (anchor && Number.isFinite(e.cx)) {
        anchor.style.transform = `translate(${e.cx.toFixed(1)}px, ${e.cy.toFixed(1)}px)`;
        // The mark steps back while the camera is looking straight at a node behind it.
        const behind = sel >= 0 && props.picture !== 'timeline' && Math.hypot(e.X[sel] - e.cx, e.Y[sel] - e.cy) < 46;
        anchor.style.opacity = behind ? '0.18' : '1';
        anchor.style.transition = 'opacity 0.25s';
      }
      cost += performance.now() - t0;
      if (++drawn % 20 === 0) {
        canvas.parentElement?.setAttribute('data-draw-ms', (cost / 20).toFixed(2));
        cost = 0;
      }
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [engine, sized, size.w, size.h]);

  const connectionsRef = useRef<Connection[]>([]);
  const litRef = useRef<{ index: number; relation: string } | null>(null);
  const lit = p.litLink ? p.globe.byKey.get(p.litLink.key) : undefined;
  litRef.current = lit && p.litLink ? { index: lit.index, relation: p.litLink.relation } : null;
  const foundRef = useRef<ReadonlySet<number> | null>(null);
  connectionsRef.current = connections;
  foundRef.current = foundIdx;
  useEffect(() => {
    engine.dirty = true;
  }, [engine, connections, foundIdx, p.selectedKey, p.pointedKey, p.colours, p.offLayers, p.offDepts, p.picture, motionOn, p.focus, p.litLink, p.expanded, p.xlinks, p.links]);

  const at = (ev: { clientX: number; clientY: number }) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };
  const labelAt = (x: number, y: number): LabelHit | undefined => labelsRef.current.find((l) => x >= l.x && x <= l.x + l.w && y >= l.y && y <= l.y + l.h);

  return (
    <div
      ref={hostRef}
      tabIndex={0}
      role="application"
      aria-roledescription={p.picture === 'globe' ? 'universe' : p.picture === 'map' ? 'map' : 'timeline'}
      aria-label={
        p.picture === 'globe'
          ? `Universe of ${p.centreFile}: ${p.globe.nodes.length.toLocaleString()} items in nine layers. Drag to turn it, scroll to zoom, plus and minus zoom, 0 shows the whole globe.`
          : p.picture === 'map'
            ? `Map of ${p.centreFile}: ${p.globe.nodes.length.toLocaleString()} items. Drag to move, scroll to zoom, plus and minus zoom, 0 fits it.`
            : `Timeline of ${p.centreFile}: every item by its last change.`
      }
      className="absolute inset-0 outline-none"
      data-memory-brain-canvas={p.picture}
      onKeyDown={(ev) => {
        const e = engine;
        const zoom = (f: number) => {
          if (p.picture === 'globe') e.cam = { ...e.cam, zoom: Math.max(0.45, Math.min(9, e.cam.zoom * f)) };
          else {
            const cx = p.insetLeft + room / 2;
            const cy = size.h / 2;
            const k = Math.max(0.02, Math.min(14, e.flat.k * f));
            const r = k / e.flat.k;
            e.flat = { k, x: cx - (cx - e.flat.x) * r, y: cy - (cy - e.flat.y) * r };
          }
          e.camTween = null;
          e.flatTween = null;
          e.dirty = true;
        };
        if (ev.key === '+' || ev.key === '=') zoom(1.25);
        else if (ev.key === '-' || ev.key === '_') zoom(0.8);
        else if (ev.key === '0') p.handleRef?.current?.fit();
        else if (ev.key === 'Escape' && p.selectedKey) {
          if (!p.onBack?.()) p.onSelect(null);
        }
        else if (ev.key === 'Enter' && p.selectedKey) p.onOpen(p.selectedKey);
        else return;
        ev.preventDefault();
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block h-full w-full touch-none"
        style={{ cursor: 'grab' }}
        onPointerDown={(ev) => {
          if (ev.button !== 0) return;
          ev.currentTarget.setPointerCapture(ev.pointerId);
          const a = at(ev);
          engine.drag = { x: a.x, y: a.y, moved: false, button: ev.button };
          hostRef.current?.focus({ preventScroll: true });
        }}
        onPointerMove={(ev) => {
          const e = engine;
          const a = at(ev);
          if (e.drag) {
            const dx = a.x - e.drag.x;
            const dy = a.y - e.drag.y;
            if (!e.drag.moved && Math.hypot(dx, dy) > 4) e.drag.moved = true;
            if (e.drag.moved) {
              e.camTween = null;
              e.flatTween = null;
              if (p.picture === 'globe') e.cam = { ...e.cam, yaw: e.cam.yaw - dx * 0.006, pitch: Math.max(-1.45, Math.min(1.45, e.cam.pitch + dy * 0.006)) };
              else e.flat = { ...e.flat, x: e.flat.x + dx, y: e.flat.y + dy };
              e.drag.x = a.x;
              e.drag.y = a.y;
              e.dirty = true;
            }
            return;
          }
          const hit = pickNode(e, p.globe, a.x, a.y, p.picture);
          const overLabel = hit < 0 && labelAt(a.x, a.y);
          if (hit !== e.hover) {
            e.hover = hit;
            e.dirty = true;
          }
          ev.currentTarget.style.cursor = hit >= 0 || overLabel ? 'pointer' : 'grab';
        }}
        onPointerUp={(ev) => {
          const e = engine;
          const d = e.drag;
          e.drag = null;
          if (!d || d.moved) return;
          const a = at(ev);
          const hit = pickNode(e, p.globe, a.x, a.y, p.picture);
          if (hit >= 0) p.onSelect(p.globe.nodes[hit].key);
          else {
            const l = labelAt(a.x, a.y);
            if (l) p.onLayerPress(l.layer);
            else p.onSelect(null);
          }
        }}
        onPointerLeave={() => {
          if (engine.hover >= 0) {
            engine.hover = -1;
            engine.dirty = true;
          }
        }}
        onDoubleClick={(ev) => {
          const a = at(ev);
          const hit = pickNode(engine, p.globe, a.x, a.y, p.picture);
          if (hit >= 0) p.onOpen(p.globe.nodes[hit].key);
        }}
        onWheel={(ev) => {
          // Only the picture zooms; a list over it scrolls itself (memoryFocusModel.ts).
          if (!wheelZoomsPicture(ev.target as Element)) return;
          const e = engine;
          const f = Math.exp(-ev.deltaY * 0.0015);
          e.camTween = null;
          e.flatTween = null;
          if (p.picture === 'globe') e.cam = { ...e.cam, zoom: Math.max(0.45, Math.min(9, e.cam.zoom * f)) };
          else {
            const a = at(ev);
            const k = Math.max(0.02, Math.min(14, e.flat.k * f));
            const r = k / e.flat.k;
            e.flat = { k, x: a.x - (a.x - e.flat.x) * r, y: a.y - (a.y - e.flat.y) * r };
          }
          e.dirty = true;
        }}
      />
    </div>
  );
}
